/**
 * The metric name the Go backend reports its own duration under.
 * See `ServerTiming` in `apps/backend/middleware/timing.go`.
 */
const APP_METRIC = 'app';

/**
 * Read the server's own duration out of a `Server-Timing` response header.
 *
 * The Go backend sends `app;dur=38.4, trace;desc="..."`, and the header is
 * listed in the router's CORS exposed headers, so this can be read straight off
 * the axios response - no Resource Timing needed for the server's share.
 *
 * Note what the number means: the backend stamps the header as response headers
 * are flushed, so it measures up to the first byte and excludes the time spent
 * streaming the body. That is the point - it is the part Resource Timing's TTFB
 * cannot separate from the network round trip.
 *
 * Returns null rather than 0 when there is nothing to read. The legacy Java app
 * sends no `Server-Timing` at all, and a 0 there would read as "the server took
 * no time" instead of "we could not tell".
 */
export function parseServerTiming(header: string | null | undefined): number | null {
  if (!header) {
    return null;
  }

  for (const metric of header.split(',')) {
    // A metric is `name;param=value;...`. Only the name and dur matter here.
    const parts = metric.split(';');
    const name = parts[0]?.trim().toLowerCase();
    if (name !== APP_METRIC) {
      continue;
    }

    for (const param of parts.slice(1)) {
      const separator = param.indexOf('=');
      if (separator === -1) {
        continue;
      }
      if (param.slice(0, separator).trim().toLowerCase() !== 'dur') {
        continue;
      }

      const value = Number(param.slice(separator + 1).trim());
      return Number.isFinite(value) ? value : null;
    }
  }

  return null;
}
